import { create } from "zustand";

const STORAGE_KEY = "hippo.designDocs";

export interface DocProject {
  id: string;
  formatId: string;
  title: string;
  /** sectionId → the user's Markdown text for that section. */
  values: Record<string, string>;
  /** Ids of other documents this one references (systems ↔ features ↔ mechanics). */
  links?: string[];
  createdAt: number;
  updatedAt: number;
}

function load(): DocProject[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) return p as DocProject[];
    }
  } catch {
    /* ignore */
  }
  return [];
}

function newId(): string {
  return `doc-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

interface State {
  documents: DocProject[];
  byId: (id: string) => DocProject | undefined;
  create: (formatId: string, opts?: { title?: string; values?: Record<string, string> }) => string;
  rename: (id: string, title: string) => void;
  setValue: (id: string, sectionId: string, text: string) => void;
  setLinks: (id: string, links: string[]) => void;
  remove: (id: string) => void;
  duplicate: (id: string) => string | undefined;
}

export const useDesignDocStore = create<State>((set, get) => {
  const persist = () => localStorage.setItem(STORAGE_KEY, JSON.stringify(get().documents));

  return {
    documents: load(),

    byId: (id) => get().documents.find((d) => d.id === id),

    create: (formatId, opts) => {
      const now = Date.now();
      const doc: DocProject = {
        id: newId(),
        formatId,
        title: opts?.title?.trim() || "Untitled game",
        values: opts?.values ?? {},
        links: [],
        createdAt: now,
        updatedAt: now,
      };
      set({ documents: [doc, ...get().documents] });
      persist();
      return doc.id;
    },

    rename: (id, title) => {
      set({
        documents: get().documents.map((d) =>
          d.id === id ? { ...d, title: title.trim() || d.title, updatedAt: Date.now() } : d
        ),
      });
      persist();
    },

    setValue: (id, sectionId, text) => {
      set({
        documents: get().documents.map((d) =>
          d.id === id ? { ...d, values: { ...d.values, [sectionId]: text }, updatedAt: Date.now() } : d
        ),
      });
      persist();
    },

    setLinks: (id, links) => {
      set({
        documents: get().documents.map((d) => (d.id === id ? { ...d, links, updatedAt: Date.now() } : d)),
      });
      persist();
    },

    remove: (id) => {
      set({ documents: get().documents.filter((d) => d.id !== id) });
      persist();
    },

    duplicate: (id) => {
      const src = get().byId(id);
      if (!src) return undefined;
      const now = Date.now();
      const copy: DocProject = {
        ...src,
        id: newId(),
        title: `${src.title} (copy)`,
        values: { ...src.values },
        links: [...(src.links ?? [])],
        createdAt: now,
        updatedAt: now,
      };
      set({ documents: [copy, ...get().documents] });
      persist();
      return copy.id;
    },
  };
});
